import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdEdit } from 'react-icons/md';
import { IoMdUnlock, IoMdTrash } from 'react-icons/io';
import toast from 'react-hot-toast';
import Skeleton from '../../../../components/Skeleton';
import TableTemplate from '../../../../components/table/index';
import CustomModal from '../../../../components/modal/index';
import { getAllUser, deleteUser } from '../../../../controllers/UserController';
import { resetPassword } from '../../../../controllers/AuthController';

const TableComponent = ({ t }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [selectedID, setSelectedID] = useState(null);
  const navigate = useNavigate();
  
  const columns = useMemo(() => [
    { field: 'name', title: t('common.name.title'), sortable: true },
    { field: 'gender', title: t('common.gender.title'), sortable: true },
    { field: 'role', title: t('common.role.title'), sortable: true },
    { field: 'username', title: t('common.username.title'), sortable: true },
    { field: 'email', title: t('common.email.title'), sortable: true },
  ], [t]);
  
  const actions = useMemo(() => [
    (row) => (
      <button
        className='focus:outline-none text-xl md:text-2xl text-secondary hover:text-primary focus:text-primary'
        onClick={() => navigate(`/user/${row.id}/edit`)}
      >
        <MdEdit />
      </button>
    ),
    (row) => (
      <button
        className='focus:outline-none text-xl md:text-2xl text-warningMessage hover:text-warningText focus:text-warningText'
        onClick={() => handleOpenResetModal(row.id)}
      >
        <IoMdUnlock />
      </button>
    ),
    (row) => (
      <button
        className='focus:outline-none text-xl md:text-2xl text-errorMessage hover:text-errorText focus:text-errorText'
        onClick={() => handleOpenModal(row.id)}
      >
        <IoMdTrash />
      </button>
    ),
  ], [navigate]);
  
  const filters = useMemo(() => [
    { field: 'role', label: t('common.role.title'), by: t('common.role.admin'), labelBy: t('common.role.admin'), mode: 'contains' },
    { field: 'role', label: t('common.role.title'), by: t('common.role.staff'), labelBy: t('common.role.staff'), mode: 'contains' },
    { field: 'gender', label: t('common.gender.title'), by: t('common.gender.male'), labelBy: t('common.gender.male'), mode: 'exact' },
    { field: 'gender', label: t('common.gender.title'), by: t('common.gender.female'), labelBy: t('common.gender.female'),  mode: 'exact' },
  ], [t]);
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        const result = await getAllUser();
        const formattedRows = result.users.map((user) => {
          const formattedGender = user.gender.toLowerCase();
          const formattedRoleName = user.role.name.toLowerCase().replace(/\s(.)/g, (match) => match.toUpperCase()).replace(/\s/g, '');
          const translatedRoleName = t(`common.role.${formattedRoleName}`, { defaultValue: user.role.name });
          
          return {
            id: user.id,
            name: user.name,
            gender: t(`common.gender.${formattedGender}`),
            role: translatedRoleName,
            username: user.username,
            email: user.email
          };
        });
        
        setRows(formattedRows);
      } catch (error) {
        toast.error(error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [t]);
  
  const handleAdd = () => {
    navigate('/user/create');
  };
  
  const handleOpenResetModal = (id) => {
    setSelectedID(id);
    setResetOpen(true);
  };
  
  const handleCloseResetModal = () => {
    setResetOpen(false);
  };
  
  const handleResetPassword = async () => {
    try {
      await resetPassword(selectedID);
      setResetOpen(false);
      toast.success(t('user.resetPassword.message'));
    } catch (error) {
      setResetOpen(false);
      toast.error(error.message);
    };
  };
  
  const handleOpenModal = (id) => {
    setSelectedID(id);
    setOpen(true);
  };
  
  const handleClose = () => {
    setOpen(false);
  };
  
  const handleDelete = async () => {
    try {
      await deleteUser(selectedID);
      setRows(rows.filter((row) => row.id !== selectedID));
      setOpen(false);
      toast.success(t('user.delete.message'));
    } catch (error) {
      setOpen(false);
      toast.error(error.message);
    };
  };
  
  if (loading) {
    return (
      <Skeleton className='h-96 w-full rounded-lg' />
    );
  };
  
  return (
    <div>
      <TableTemplate
        rows={rows}
        columns={columns}
        actions={actions}
        filters={filters}
        onAddNew={handleAdd}
        fileName={t('menu.user')}
      />
      
      {/* Modal Reset Password */}
      <CustomModal
        open={resetOpen}
        onClose={handleCloseResetModal}
        onConfirm={handleResetPassword}
        title={t('user.resetPassword.title')}
        description={t('user.resetPassword.description')}
        confirmText={t('common.action.reset')}
        cancelText={t('common.action.cancel')}
      />
      
      {/* Modal Delete */}
      <CustomModal
        open={open}
        onClose={handleClose}
        onConfirm={handleDelete}
        title={t('user.delete.title')}
        description={t('user.delete.description')}
        confirmText={t('common.action.delete')}
        cancelText={t('common.action.cancel')}
      />
    </div>
  );
};

export default TableComponent;
